import React, { useEffect, useState } from 'react';
import { Button } from '@/components';
import { SideBarModal } from '@/components/Modal';
import { Maintenance } from '@/types';
import { getMaintenanceCategoryLabel } from '@/utils';

interface RequestPermSwapModalProps {
  isOpen: boolean;
  onClose: () => void;
  onNext: (selection: 'NEW' | 'EXISTING') => void;
  isSubmitting?: boolean;
  maintenance: Maintenance;
}

const RequestPermSwapModal: React.FC<RequestPermSwapModalProps> = ({
  isOpen,
  onClose,
  onNext,
  maintenance,
  isSubmitting = false,
}) => {
  const [selection, setSelection] = useState<'NEW' | 'EXISTING' | ''>('');
  const maintenanceDetails = Array.isArray(maintenance.categories)
    ? maintenance.categories
        .map((category) => getMaintenanceCategoryLabel(category as any))
        .join(', ')
    : '-';
  useEffect(() => {
    if (!isOpen) {
      setSelection('');
    }
  }, [isOpen]);

  const handleNext = () => {
    if (!selection) return;
    onNext(selection);
  };

  return (
    <SideBarModal
      isOpen={isOpen}
      onClose={onClose}
      position="center"
      width="sm"
      maxHeight="screen"
      showCloseButton
      className="min-w-[600px]"
      header={
        <div>
          <div className="text-base font-semibold text-gray-900">
            Permanent Vehicle Swap
          </div>
          <div className="text-xs text-gray-500">
            Select the replacement vehicle source to proceed.
          </div>
        </div>
      }
      footer={
        <div className="flex w-full justify-end gap-3">
          <Button
            onClick={onClose}
            className="rounded-xl border border-gray-200 bg-white px-4 py-2 text-gray-600 hover:bg-gray-50"
            variant="secondary"
          >
            Cancel
          </Button>
          <Button
            onClick={handleNext}
            disabled={!selection || isSubmitting}
            className="rounded-xl bg-purple-600 px-5 py-2 text-white hover:bg-purple-700"
          >
            {isSubmitting ? 'Saving...' : 'Next'}
          </Button>
        </div>
      }
    >
      <div className="bg-gray-50 px-4 py-2 text-[11px] font-medium text-gray-500 rounded-md">
        VEHICLE DETAILS
      </div>

      <div className="px-6 py-4 space-y-3">
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <div className="text-[11px] text-gray-500">Old Vehicle</div>
            <div className="font-medium text-gray-900">{`${
              maintenance.vehiclePlatNomor || '-'
            } - ${maintenance.vehicleOemName || '-'} - ${
              maintenance.vehicleModelName || '-'
            }`}</div>
          </div>
          <div>
            <div className="text-[11px] text-gray-500">Maintenance Details</div>
            <div className="font-medium text-gray-900">
              {maintenanceDetails}
            </div>
          </div>
        </div>
        <div>
          <div className="text-[11px] text-gray-500">Notes</div>
          <div className="text-sm text-gray-900">
            {maintenance.notes || '-'}
          </div>
        </div>
      </div>

      <div className="bg-gray-50 px-4 py-2 text-[11px] font-medium text-gray-500 rounded-md">
        DECISION
      </div>

      <div className="px-6 py-4 space-y-3">
        <div className="text-sm text-gray-900">
          Has the replacement vehicle already been added to the system?
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <button
            type="button"
            onClick={() => setSelection('NEW')}
            className={`flex items-center justify-between rounded-xl border px-4 py-3 text-left text-sm transition ${
              selection === 'NEW'
                ? 'border-purple-500 bg-white shadow-sm'
                : 'border-gray-200 bg-white'
            }`}
          >
            <span>Create new vehicle now</span>
            <span
              className={`inline-flex h-4 w-4 items-center justify-center rounded-full border ${
                selection === 'NEW'
                  ? 'border-purple-500 text-purple-600'
                  : 'border-gray-300 text-transparent'
              }`}
            >
              <span className="h-2 w-2 rounded-full bg-current" />
            </span>
          </button>
          <button
            type="button"
            onClick={() => setSelection('EXISTING')}
            className={`flex items-center justify-between rounded-xl border px-4 py-3 text-left text-sm transition ${
              selection === 'EXISTING'
                ? 'border-purple-500 bg-white shadow-sm'
                : 'border-gray-200 bg-white'
            }`}
          >
            <span>Select from existing vehicles</span>
            <span
              className={`inline-flex h-4 w-4 items-center justify-center rounded-full border ${
                selection === 'EXISTING'
                  ? 'border-purple-500 text-purple-600'
                  : 'border-gray-300 text-transparent'
              }`}
            >
              <span className="h-2 w-2 rounded-full bg-current" />
            </span>
          </button>
        </div>
      </div>
    </SideBarModal>
  );
};

export default RequestPermSwapModal;
